import React, { useState } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Eye, EyeOff, User, Mail, Phone, Lock, AlertCircle, CheckCircle, Key } from 'lucide-react';
import useWindowSize, { isMobile } from '../../hooks/useWindowSize';
import {
  loadingToggleAction,
  signupAction,
  socialLoginAction,
  clearAuthMessages,
} from '../../store/actions/AuthActions';
import loginbg from "../../images/bg-login.jpg";

// Check if signup codes feature is enabled (defaults to true)
const SIGNUP_CODES_ENABLED = process.env.REACT_APP_SIGNUP_CODES_ENABLED !== 'false';

// Enhanced CSS styles
const styles = {
  loginMainPage: {
    minHeight: '100vh',
    display: 'flex',
    backgroundImage: `url(${loginbg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  },
  loginWrapper: {
    display: 'flex',
    width: '100%',
    minHeight: '100vh'
  },
  loginAsideLeft: {
    flex: '1',
    background: 'rgba(0, 0, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    color: 'white'
  },
  loginAsideRight: {
    flex: '1',
    background: 'rgba(255, 255, 255, 0.98)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    backdropFilter: 'blur(10px)',
    overflowY: 'auto'
  },
  authCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '3rem',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    width: '100%',
    maxWidth: '500px',
    border: '1px solid #e5e7eb',
    margin: '2rem 0'
  },
  formGroup: {
    marginBottom: '1.5rem',
    position: 'relative'
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  formInput: {
    width: '100%',
    padding: '0.875rem 1rem 0.875rem 3rem',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.2s ease',
    backgroundColor: '#fafafa'
  },
  inputIcon: {
    position: 'absolute',
    left: '1rem',
    color: '#9ca3af',
    zIndex: 2
  },
  eyeIcon: {
    position: 'absolute',
    right: '1rem',
    color: '#9ca3af',
    cursor: 'pointer',
    zIndex: 2
  },
  submitButton: {
    width: '100%',
    padding: '0.875rem',
    background: 'var(--bs-primary)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    transition: 'all 0.2s ease',
    marginTop: '1rem'
  },
  socialButton: {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#fff',
    color: '#374151',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    transition: 'all 0.2s ease',
    cursor: 'pointer'
  },
  separator: {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    color: '#9ca3af',
    margin: '2rem 0 1.5rem 0'
  },
  separatorLine: {
    flex: 1,
    height: '1px',
    background: '#e5e7eb'
  },
  separatorText: {
    padding: '0 1rem',
    fontSize: '0.875rem',
    backgroundColor: 'white'
  },
  alertMessage: {
    padding: '0.875rem 1rem',
    borderRadius: '8px',
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    fontSize: '0.875rem'
  },
  errorAlert: {
    backgroundColor: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca'
  },
  successAlert: {
    backgroundColor: '#f0fdf4',
    color: '#16a34a',
    border: '1px solid #bbf7d0'
  },
  loadingSpinner: {
    width: '20px',
    height: '20px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid currentColor',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }
};

// Mobile-specific style overrides (applied when screen ≤ 768px)
const mobileStyles = {
  loginWrapper: {
    flexDirection: 'column'
  },
  loginAsideLeft: {
    minHeight: '200px',
    padding: '2rem 1.5rem',
    flex: 'none'
  },
  loginAsideRight: {
    padding: '1.5rem 1rem',
    flex: 'none'
  },
  authCard: {
    padding: '2rem 1.5rem',
    boxShadow: 'none',
    borderRadius: '0'
  },
  formInput: {
    fontSize: '16px' // Prevents zoom on iOS
  }
};

// Helper function to merge mobile styles
const getResponsiveStyle = (baseStyle, mobileStyle, isMobile) => {
  return isMobile && mobileStyle ? { ...baseStyle, ...mobileStyle } : baseStyle;
};

function Register(props) {
  // Mobile responsiveness
  const { width } = useWindowSize();
  const mobile = isMobile(width);

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const redirectUrl = searchParams.get('redirect') || '/';
  const urlSignupCode = searchParams.get('code') || '';

  // Handle Google signup with signup code
  const handleGoogleSignup = () => {
    const signupCode = formik.values.signupCode?.trim();

    // Only validate signup code if feature is enabled
    if (SIGNUP_CODES_ENABLED) {
      if (!signupCode) {
        formik.setFieldError('signupCode', 'Du måste ange en registreringskod för att registrera dig med Google');
        formik.setFieldTouched('signupCode', true);
        return;
      }

      // Validate UUID format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(signupCode)) {
        formik.setFieldError('signupCode', 'Ogiltig registreringskod');
        formik.setFieldTouched('signupCode', true);
        return;
      }
    }

    setIsLoading(true);
    dispatch(loadingToggleAction(true));
    dispatch(socialLoginAction('google', history, redirectUrl, signupCode || null));
  };

  // UUID validation regex
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Formik form handling and validation
  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      password: '',
      signupCode: urlSignupCode,
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .min(2, 'Förnamnet måste vara minst 2 tecken')
        .required('Du måste ange ett förnamn'),
      lastName: Yup.string()
        .min(2, 'Efternamnet måste vara minst 2 tecken')
        .required('Du måste ange ett efternamn'),
      phone: Yup.string()
        .matches(/^[0-9]{10}$/, 'Telefonnumret måste vara 10 siffror')
        .required('Du måste ange ett telefonnummer'),
      email: Yup.string()
        .email('Ogiltig e-postadress')
        .required('Du måste ange en e-postadress'),
      password: Yup.string()
        .min(8, 'Lösenordet måste vara minst 8 tecken')
        .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Lösenordet måste innehålla minst en stor bokstav, en liten bokstav och en siffra')
        .required('Du måste ange ett lösenord'),
      signupCode: SIGNUP_CODES_ENABLED
        ? Yup.string()
          .required('Du måste ange en registreringskod')
          .matches(uuidRegex, 'Ogiltig registreringskod')
        : Yup.string(),
    }),
    onSubmit: (values) => {
      setIsLoading(true);
      dispatch(loadingToggleAction(true));
      dispatch(
        signupAction(
          values.firstName,
          values.lastName,
          values.phone,
          values.email,
          values.password,
          values.signupCode,
          props.history,
          redirectUrl
        )
      );
    },
  });

  // Clear any lingering error messages from other pages (e.g., login page) on mount
  React.useEffect(() => {
    dispatch(clearAuthMessages());
  }, [dispatch]);

  // Reset loading state when component updates
  React.useEffect(() => {
    if (!props.showLoading) {
      setIsLoading(false);
    }
  }, [props.showLoading]);

  return (
    <>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .social-button:hover:not(:disabled) {
          border-color: var(--bs-primary);
          background-color: #f8fafc;
        }

        .social-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .form-input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          background-color: white;
        }
        
        .submit-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        }
        
        .submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        
        .error-input {
          border-color: #dc2626 !important;
          background-color: #fef2f2 !important;
        }
      `}</style>

      <div style={styles.loginMainPage} data-testid="registration-page">
        <div style={getResponsiveStyle(styles.loginWrapper, mobileStyles.loginWrapper, mobile)}>
          {/* Left Side */}
          <div className="login-aside-left" style={getResponsiveStyle(styles.loginAsideLeft, mobileStyles.loginAsideLeft, mobile)} data-testid="registration-branding">
            <div style={{ textAlign: 'center', maxWidth: '400px' }}>
              <h1
                style={{
                  fontSize: '3rem',
                  fontWeight: '700',
                  marginBottom: '1rem',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                  color: 'white'
                }}
                data-testid="app-title"
              >
                LedgerLoop AI
              </h1>
              <p
                style={{
                  fontSize: '1.25rem',
                  marginBottom: '3rem',
                  opacity: 0.95,
                  textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                  lineHeight: 1.4
                }}
                data-testid="app-description"
              >
                Allt-i-ett lösning för bokföring, löner, fakturor och rapporter
              </p>

              <div style={{
                marginTop: 'auto',
                fontSize: '0.875rem',
                opacity: 0.8,
                textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
              }}>
                <p style={{ marginBottom: '1rem' }} data-testid="copyright">
                  © 2026 Riza Consulting AB
                </p>
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  <a
                    href="https://www.ledgerloop.ai/privacy"
                    style={{
                      color: 'rgba(255,255,255,0.9)',
                      textDecoration: 'none',
                      fontSize: '0.875rem'
                    }}
                    data-testid="privacy-link"
                  >
                    Integritetspolicy
                  </a>
                  <span style={{ opacity: 0.7 }}>•</span>
                  <a
                    href="https://www.ledgerloop.ai/terms"
                    style={{
                      color: 'rgba(255,255,255,0.9)',
                      textDecoration: 'none',
                      fontSize: '0.875rem'
                    }}
                    data-testid="terms-link"
                  >
                    Användarvillkor
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side */}
          <div className="login-aside-right" style={getResponsiveStyle(styles.loginAsideRight, mobileStyles.loginAsideRight, mobile)} data-testid="registration-form-container">
            <div style={getResponsiveStyle(styles.authCard, mobileStyles.authCard, mobile)} data-testid="auth-card">
              <div style={{ marginBottom: '2rem', textAlign: 'center' }} data-testid="registration-header">
                <h2 style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937', marginBottom: '0.5rem' }}>
                  Skapa ditt konto
                </h2>
                <p style={{ color: '#6b7280', fontSize: '1rem' }}>
                  Registrera dig gratis och kom igång på några minuter
                </p>
              </div>

              {/* Alert Messages */}
              {props.errorMessage && (
                <div style={{ ...styles.alertMessage, ...styles.errorAlert }} data-testid="error-message" role="alert">
                  <AlertCircle size={20} />
                  {props.errorMessage}
                </div>
              )}

              {props.successMessage && (
                <div style={{ ...styles.alertMessage, ...styles.successAlert }} data-testid="success-message" role="alert">
                  <CheckCircle size={20} />
                  {props.successMessage}
                </div>
              )}

              {/* Signup Code - Required for all registration methods (only shown when feature is enabled) */}
              {SIGNUP_CODES_ENABLED && (
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '1.5rem'
                }}>
                  <label
                    htmlFor="signupCode-input"
                    style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}
                  >
                    Registreringskod <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                    Krävs för alla registreringsmetoder
                  </p>
                  <div style={styles.inputWrapper}>
                    <Key size={20} style={styles.inputIcon} />
                    <input
                      id="signupCode-input"
                      data-testid="signupCode-input"
                      type="text"
                      name="signupCode"
                      className={`form-input ${formik.touched.signupCode && formik.errors.signupCode ? 'error-input' : ''}`}
                      style={styles.formInput}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.signupCode}
                      disabled={props.showLoading || isLoading}
                    />
                  </div>
                  {formik.touched.signupCode && formik.errors.signupCode && (
                    <div
                      style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}
                      data-testid="signupCode-error"
                    >
                      {formik.errors.signupCode}
                    </div>
                  )}
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    Har du ingen kod? <a href="https://www.ledgerloop.ai/earlyaccess" style={{ color: '#3b82f6' }}>Begär åtkomst här</a>
                  </p>
                </div>
              )}

              {/* Google Signup Button */}
              <button
                type="button"
                data-testid="google-signup-button"
                className="social-button"
                style={{
                  ...styles.socialButton,
                  backgroundColor: '#4285f4',
                  color: 'white',
                  border: 'none'
                }}
                onClick={handleGoogleSignup}
                disabled={props.showLoading || isLoading || (SIGNUP_CODES_ENABLED && !formik.values.signupCode)}
              >
                <i className="fab fa-google" style={{ color: 'white' }}></i>
                Registrera med Google
              </button>

              {/* Separator for email registration */}
              <div style={styles.separator} data-testid="email-separator">
                <div style={styles.separatorLine}></div>
                <span style={styles.separatorText}>eller registrera med e-post</span>
                <div style={styles.separatorLine}></div>
              </div>

              {/* Email Registration Form */}
              <form onSubmit={formik.handleSubmit} data-testid="registration-form" role="form">
                {/* First Name & Last Name Row */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label
                      htmlFor="firstName-input"
                      style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}
                    >
                      Förnamn
                    </label>
                    <div style={styles.inputWrapper}>
                      <User size={20} style={styles.inputIcon} />
                      <input
                        id="firstName-input"
                        data-testid="firstName-input"
                        type="text"
                        name="firstName"
                        className={`form-input ${formik.touched.firstName && formik.errors.firstName ? 'error-input' : ''}`}
                        style={styles.formInput}
                        placeholder="Förnamn"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.firstName}
                        disabled={props.showLoading || isLoading}
                      />
                    </div>
                    {formik.touched.firstName && formik.errors.firstName && (
                      <div
                        style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}
                        data-testid="firstName-error"
                      >
                        {formik.errors.firstName}
                      </div>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="lastName-input"
                      style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}
                    >
                      Efternamn
                    </label>
                    <div style={styles.inputWrapper}>
                      <User size={20} style={styles.inputIcon} />
                      <input
                        id="lastName-input"
                        data-testid="lastName-input"
                        type="text"
                        name="lastName"
                        className={`form-input ${formik.touched.lastName && formik.errors.lastName ? 'error-input' : ''}`}
                        style={styles.formInput}
                        placeholder="Efternamn"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.lastName}
                        disabled={props.showLoading || isLoading}
                      />
                    </div>
                    {formik.touched.lastName && formik.errors.lastName && (
                      <div
                        style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}
                        data-testid="lastName-error"
                      >
                        {formik.errors.lastName}
                      </div>
                    )}
                  </div>
                </div>

                {/* Email */}
                <div style={styles.formGroup}>
                  <label
                    htmlFor="email-input"
                    style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}
                  >
                    E-postadress
                  </label>
                  <div style={styles.inputWrapper}>
                    <Mail size={20} style={styles.inputIcon} />
                    <input
                      id="email-input"
                      data-testid="email-input"
                      type="email"
                      name="email"
                      className={`form-input ${formik.touched.email && formik.errors.email ? 'error-input' : ''}`}
                      style={styles.formInput}
                      placeholder="din@epost.se"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.email}
                      disabled={props.showLoading || isLoading}
                    />
                  </div>
                  {formik.touched.email && formik.errors.email && (
                    <div
                      style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}
                      data-testid="email-error"
                    >
                      {formik.errors.email}
                    </div>
                  )}
                </div>

                {/* Phone */}
                <div style={styles.formGroup}>
                  <label
                    htmlFor="phone-input"
                    style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}
                  >
                    Telefonnummer
                  </label>
                  <div style={styles.inputWrapper}>
                    <Phone size={20} style={styles.inputIcon} />
                    <input
                      id="phone-input"
                      data-testid="phone-input"
                      type="text"
                      name="phone"
                      className={`form-input ${formik.touched.phone && formik.errors.phone ? 'error-input' : ''}`}
                      style={styles.formInput}
                      placeholder="0721234567"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.phone}
                      disabled={props.showLoading || isLoading}
                    />
                  </div>
                  {formik.touched.phone && formik.errors.phone && (
                    <div
                      style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}
                      data-testid="phone-error"
                    >
                      {formik.errors.phone}
                    </div>
                  )}
                </div>

                {/* Password */}
                <div style={styles.formGroup}>
                  <label
                    htmlFor="password-input"
                    style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}
                  >
                    Lösenord
                  </label>
                  <div style={styles.inputWrapper}>
                    <Lock size={20} style={styles.inputIcon} />
                    <input
                      id="password-input"
                      data-testid="password-input"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      className={`form-input ${formik.touched.password && formik.errors.password ? 'error-input' : ''}`}
                      style={{ ...styles.formInput, paddingRight: '3rem' }}
                      placeholder="Minst 8 tecken med stor bokstav, liten bokstav och siffra"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.password}
                      disabled={props.showLoading || isLoading}
                    />
                    <button
                      type="button"
                      data-testid="password-toggle"
                      style={{ ...styles.eyeIcon, background: 'none', border: 'none' }}
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={props.showLoading || isLoading}
                      aria-label={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <div
                      style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}
                      data-testid="password-error"
                    >
                      {formik.errors.password}
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  data-testid="registration-submit-button"
                  className="submit-button"
                  style={styles.submitButton}
                  disabled={props.showLoading || isLoading}
                >
                  {(props.showLoading || isLoading) ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={styles.loadingSpinner} data-testid="loading-spinner"></div>
                      Skapar konto...
                    </div>
                  ) : (
                    'Skapa konto med e-post'
                  )}
                </button>
              </form>

              {/* Login Link */}
              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                  Har du redan ett konto?
                </p>
                <Link
                  to={redirectUrl !== '/' ? `/login?redirect=${encodeURIComponent(redirectUrl)}` : '/login'}
                  style={{ color: '#3b82f6', fontWeight: '600', textDecoration: 'none', fontSize: '1rem' }}
                  data-testid="login-link"
                >
                  Logga in här
                </Link>
              </div>

              {/* Terms */}
              <div style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.75rem', color: '#9ca3af' }} data-testid="terms-footer">
                Genom att skapa ett konto godkänner du våra{' '}
                <a href="https://www.ledgerloop.ai/legal/almanna-vilkor" style={{ color: '#3b82f6' }} data-testid="terms-footer-link">
                  användarvillkor
                </a>{' '}
                och{' '}
                <a href="https://www.ledgerloop.ai/legal/integritetspolicy" style={{ color: '#3b82f6' }} data-testid="privacy-footer-link">
                  integritetspolicy
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    errorMessage: state.auth.errorMessage,
    successMessage: state.auth.successMessage,
    showLoading: state.auth.showLoading,
  };
};

export default connect(mapStateToProps)(Register);
